/**
 * Log a caught request failure at the right severity.
 *
 * Navigating away aborts every in-flight fetch with the same
 * `TypeError: Failed to fetch` as a genuine network failure, so the error
 * object alone cannot tell "the user left the page" apart from "the backend
 * is broken". The browser fires `beforeunload`/`pagehide` before those
 * rejections are delivered, so a flag set there discriminates: during unload
 * the failure is expected noise (warn — the acceptance suite's empty-console
 * assert ignores warnings), at any other time it is a real error and must
 * stay error-level so tests and humans see it.
 *
 * The flag must not outlive the unload attempt: a cancelable beforeunload
 * (the unsaved-changes prompt) fires even when the user then stays, and a
 * bfcache restore resumes a page whose pagehide already fired. Both would
 * otherwise leave every later genuine failure demoted to warn for the rest
 * of the page's life. Hence the short auto-reset after beforeunload and the
 * pageshow reset on restore; abort rejections of a real navigation are
 * delivered well inside that window.
 */
let pageUnloading = false;
let resetTimer = null;

if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        pageUnloading = true;
        clearTimeout(resetTimer);
        resetTimer = setTimeout(() => { pageUnloading = false; }, 5000);
    });
    window.addEventListener('pagehide', () => {
        pageUnloading = true;
        clearTimeout(resetTimer);
    });
    window.addEventListener('pageshow', (e) => {
        if (e.persisted) pageUnloading = false;
    });
}

// A navigation abort surfaces as an AbortError or as fetch's TypeError. Anything
// else (a thrown string, a state error, a Response) cannot be the abort, so it
// stays error-level even during unload — the timing flag alone would demote every
// error for its whole window, and the ATS shows a cancelable leave prompt on any
// page with unsaved content, making "prompted, stayed, kept working" a real flow.
const looksAborted = (e) => e?.name === 'AbortError' || e instanceof TypeError;

const logAbortableError = (...args) => {
    const err = args[args.length - 1];
    (pageUnloading && looksAborted(err) ? console.warn : console.error)(...args);
};

export default logAbortableError;
